<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useTranslation } from "i18next-vue";
import type { ReportImage } from "@/types/report-images.types";
import {
  useRemoveReportImageFromFinding,
  useAddManyReportImagesToFinding,
} from "@/stores/queries/report-images.query";
import { useImageFiles } from "@/stores/queries/files.query";
import DRBtn from "@/components/UI/DRBtn.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import GaleryModal from "../../../galery/GaleryModal.vue";
import EditReportImageDialog from "../../photos/EditReportImageDialog.vue";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";

const props = defineProps<{
  findingId: number;
  diagnosticId: number;
  reportId: number;
  sectionId: number | null;
  images: ReportImage[];
}>();

const { t } = useTranslation();

const reportIdRef = computed(() => props.reportId);

// Current image index
const currentIndex = ref(0);

// Reset index when images change
watch(
  () => props.images,
  (newImages) => {
    if (currentIndex.value >= newImages.length) {
      currentIndex.value = Math.max(0, newImages.length - 1);
    }
  },
);

// Current image
const currentImage = computed(() => props.images[currentIndex.value]);

// Get fileIds from the nested diagnosticImage
const allFileIds = computed(() =>
  props.images.map(
    (img) => img.diagnosticImage?.fileId ?? img.diagnosticImageId,
  ),
);
const { data: imageUrls } = useImageFiles(allFileIds);

// Current image URL from the batch loaded URLs
const currentImageUrl = computed(() => imageUrls.value?.[currentIndex.value]);
const isLoadingImage = computed(
  () => !imageUrls.value || imageUrls.value[currentIndex.value] === undefined,
);

// Navigation
const canGoPrev = computed(() => currentIndex.value > 0);
const canGoNext = computed(() => currentIndex.value < props.images.length - 1);

const goPrev = () => {
  if (canGoPrev.value) currentIndex.value--;
};

const goNext = () => {
  if (canGoNext.value) currentIndex.value++;
};

const goToIndex = (index: number) => {
  currentIndex.value = index;
};

// Gallery modal
const showGallery = ref(false);

// Edit dialog
const showEditDialog = ref(false);

// Remove confirmation modal
const showRemoveConfirm = ref(false);

// Mutations
const { mutateAsync: removeImage, isPending: isRemoving } =
  useRemoveReportImageFromFinding(reportIdRef);

const { mutateAsync: addImages, isPending: isAddingImage } =
  useAddManyReportImagesToFinding(reportIdRef);

const confirmRemoveImage = () => {
  showRemoveConfirm.value = true;
};

const handleRemoveImage = async () => {
  if (!currentImage.value) return;
  await removeImage({
    imageId: currentImage.value.id,
    findingId: props.findingId,
  });
  showRemoveConfirm.value = false;
};

const handleAddImages = async (reportImageIds: number[]) => {
  if (reportImageIds.length === 0) return;
  await addImages({
    findingId: props.findingId,
    imageIds: reportImageIds,
  });
  showGallery.value = false;
};

// Image viewer
const showViewer = ref(false);
const viewerUrls = computed(() =>
  currentImageUrl.value ? [currentImageUrl.value] : [],
);
</script>

<template>
  <div class="finding-images-tab h-100 d-flex flex-column">
    <!-- Empty state -->
    <div
      v-if="!images.length"
      class="empty-state flex-grow-1 d-flex flex-column align-items-center justify-content-center"
    >
      <el-icon size="64" class="text-muted mb-3">
        <i class="bi-images"></i>
      </el-icon>
      <p class="text-muted mb-4">
        {{ t("report:findings.imagesTab.noImages") }}
      </p>
      <DRBtn
        variant="outline"
        icon="bi-plus-lg"
        :label="t('report:findings.imagesTab.addImage')"
        @click="showGallery = true"
      />
    </div>

    <!-- Images carousel -->
    <template v-else>
      <!-- Thumbnails -->
      <div class="thumbnails-bar d-flex gap-2 p-3 border-bottom">
        <div
          v-for="(img, index) in images"
          :key="img.id"
          class="thumbnail"
          :class="{ active: index === currentIndex }"
          @click="goToIndex(index)"
        >
          <img
            v-if="imageUrls?.[index]"
            :src="imageUrls[index]"
            alt=""
            class="thumbnail-img"
            draggable="false"
          />
          <el-skeleton v-else style="width: 100%; height: 100%" animated>
            <template #template>
              <el-skeleton-item
                variant="image"
                style="width: 100%; height: 100%"
              />
            </template>
          </el-skeleton>
        </div>
        <div
          class="thumbnail add-thumbnail d-flex align-items-center justify-content-center"
          @click="showGallery = true"
        >
          <el-icon size="24"><i class="bi-plus-lg"></i></el-icon>
        </div>
      </div>

      <!-- Main image area -->
      <div class="main-image-area flex-grow-1 position-relative">
        <!-- Navigation arrows -->
        <button v-if="canGoPrev" class="nav-arrow nav-prev" @click="goPrev">
          <el-icon size="32"><i class="bi-chevron-left"></i></el-icon>
        </button>

        <button v-if="canGoNext" class="nav-arrow nav-next" @click="goNext">
          <el-icon size="32"><i class="bi-chevron-right"></i></el-icon>
        </button>

        <!-- Image display -->
        <div
          class="image-container d-flex flex-column align-items-center h-100"
        >
          <!-- Image info (numbering & description) -->
          <div
            v-if="
              currentImage &&
              (currentImage.numbering || currentImage.description)
            "
            class="image-info flex-shrink-0 text-center"
          >
            <div v-if="currentImage.numbering" class="image-numbering">
              {{ t("report:photos.column.numbering") }}:
              {{ currentImage.numbering }}
            </div>
            <div v-if="currentImage.description" class="image-description">
              {{ currentImage.description }}
            </div>
          </div>

          <div
            class="image-wrapper flex-grow-1 d-flex align-items-center justify-content-center"
          >
            <el-skeleton
              v-if="isLoadingImage"
              style="width: 80%; max-width: 600px; aspect-ratio: 4/3"
              :loading="true"
              animated
            >
              <template #template>
                <el-skeleton-item
                  variant="image"
                  style="width: 100%; height: 100%"
                />
              </template>
            </el-skeleton>

            <img
              v-else-if="currentImageUrl"
              :src="currentImageUrl"
              alt=""
              class="main-image cursor-pointer"
              draggable="false"
              @click="showViewer = true"
            />
          </div>
        </div>

        <!-- Image counter -->
        <div class="image-counter">
          {{ currentIndex + 1 }} / {{ images.length }}
        </div>
      </div>

      <!-- Actions bar -->
      <div
        class="actions-bar d-flex justify-content-center gap-3 p-3 border-top"
      >
        <DRBtnIcon
          icon="bi-x-circle"
          variant="flat-danger"
          :size="40"
          @click="confirmRemoveImage"
        />
        <DRBtnIcon
          icon="bi-pencil"
          variant="flat"
          :size="40"
          @click="showEditDialog = true"
        />
        <DRBtnIcon
          icon="bi-eye"
          variant="flat"
          :size="40"
          @click="showViewer = true"
        />
      </div>
    </template>

    <!-- Gallery modal -->
    <GaleryModal
      v-model="showGallery"
      :diagnosticId="diagnosticId"
      :reportId="reportId"
      :loading="isAddingImage"
      :priority-section-id="sectionId ?? undefined"
      :exclude-finding-id="findingId"
      :title="t('report:findings.selectPhoto')"
      :message="t('report:findings.selectPhotoDesc')"
      :confirmLabel="t('report:findings.addPhoto')"
      multiple
      @confirm="handleAddImages"
    />

    <!-- Image viewer -->
    <el-image-viewer
      v-if="showViewer"
      :url-list="viewerUrls"
      hide-on-click-modal
      teleported
      @close="showViewer = false"
    />

    <!-- Edit dialog -->
    <EditReportImageDialog
      v-model="showEditDialog"
      :image="currentImage"
      :report-id="reportId"
    />

    <!-- Remove confirmation modal -->
    <ConfirmationModal
      v-model="showRemoveConfirm"
      :title="t('report:findings.imagesTab.removeConfirm.title')"
      :message="t('report:findings.imagesTab.removeConfirm.message')"
      :confirmLabel="t('report:findings.imagesTab.removeConfirm.confirm')"
      :loading="isRemoving"
      @confirm="handleRemoveImage"
    />
  </div>
</template>

<style lang="scss" scoped>
.finding-images-tab {
  background-color: var(--bs-gray-100);
}

.empty-state {
  padding: 48px;
}

.thumbnails-bar {
  background-color: var(--bs-white);
  overflow-x: auto;
  flex-shrink: 0;
}

.thumbnail {
  width: 60px;
  height: 45px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.15s;
  flex-shrink: 0;

  &.active {
    border-color: var(--bs-primary);
  }

  &:hover:not(.active) {
    border-color: var(--bs-gray-400);
  }
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.add-thumbnail {
  background-color: var(--bs-gray-200);
  color: var(--bs-gray-600);

  &:hover {
    background-color: var(--bs-gray-300);
  }
}

.main-image-area {
  min-height: 0;
}

.image-container {
  padding: 24px 60px;
  overflow: hidden;
}

.image-wrapper {
  min-height: 0;
  width: 100%;
}

.main-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.nav-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background-color: rgba(255, 255, 255, 0.9);
  color: var(--bs-gray-700);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &:hover {
    background-color: var(--bs-white);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
}

.nav-prev {
  left: 16px;
}

.nav-next {
  right: 16px;
}

.image-counter {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
}

.image-info {
  max-width: 80%;
  padding-bottom: 12px;
}

.image-numbering {
  font-size: 14px;
  font-weight: 600;
  color: var(--bs-body-color);
}

.image-description {
  font-size: 13px;
  color: var(--bs-secondary);
  white-space: pre-line;
  line-height: 1.4;
}

.actions-bar {
  background-color: var(--bs-white);
  flex-shrink: 0;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
